import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/user/user.selector';
import {
    listenToShowCards,
    listenToShowState,
    updateShowState,
    updateShowCard,
    removeCardFromShow,
    deleteShow,
} from '../utils/firebase/whatnotShow';

/**
 * Subscribes to a show's card list and state document.
 *
 * Cards live at users/{uid}/shows/{showId}/cards  (independent of the queue).
 * Show state lives at users/{uid}/shows/{showId}.
 *
 * @param {string} showId - The show document ID (NOT the user's UID)
 * @returns {{ show, cards, isLoading, setCurrentCardIndex, updateCard, updateShowName }}
 */
export function useShow(showId) {
    const user = useSelector(selectUser);
    const uid  = user?.uid;

    const [cards, setCards]       = useState([]);
    const [show, setShow]         = useState({ currentCardIndex: 0 });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!uid || !showId) return;

        setIsLoading(true);
        let cardsReady = false;
        let showReady  = false;

        const tryFinish = () => {
            if (cardsReady && showReady) setIsLoading(false);
        };

        const unsubCards = listenToShowCards(uid, showId, (c) => {
            setCards(c);
            cardsReady = true;
            tryFinish();
        });

        const unsubShow = listenToShowState(uid, showId, (s) => {
            setShow(s);
            showReady = true;
            tryFinish();
        });

        return () => {
            unsubCards();
            unsubShow();
        };
    }, [uid, showId]);

    /** Persist a new currentCardIndex to the show document. */
    const setCurrentCardIndex = useCallback(
        (index) => updateShowState(uid, showId, { currentCardIndex: index }),
        [uid, showId]
    );

    /** Persist show-specific card metadata (sold, finalBid, notes). */
    const updateCard = useCallback(
        (cardId, data) => updateShowCard(uid, showId, cardId, data),
        [uid, showId]
    );

    /** Persist a name change to the show document. */
    const updateShowName = useCallback(
        (name) => updateShowState(uid, showId, { name }),
        [uid, showId]
    );

    /** Persist arbitrary fields to the show document. */
    const updateShow = useCallback(
        (data) => updateShowState(uid, showId, data),
        [uid, showId]
    );

    /** Remove a card from the show's cards subcollection. */
    const removeCard = useCallback(
        (cardId) => removeCardFromShow(uid, showId, cardId),
        [uid, showId]
    );

    /** Delete the entire show and all its cards. */
    const deleteCurrentShow = useCallback(
        () => deleteShow(uid, showId),
        [uid, showId]
    );

    return { show, cards, isLoading, setCurrentCardIndex, updateCard, updateShowName, updateShow, removeCard, deleteCurrentShow };
}
